/**
 * Pure helpers for the list-structure ("renumber") capability.
 *
 * Kept free of any Word/Office dependency so they can be unit-tested in
 * isolation and imported by both the apply services and the React preview.
 */

import {
  EditOperation,
  ListChange,
  NumberingChangeKind,
} from "../../../interfaces/autoRedlineTypes";

/**
 * Resolve the effective ListChange for an edit. The backend emits `listChange`
 * on every edit (`null` when there is no structure change), so this normalizes
 * that `null` to `undefined` and is the single accessor the apply layer uses.
 */
export function resolveListChange(edit: EditOperation): ListChange | undefined {
  return edit.listChange ?? undefined;
}

/** Human-readable one-liner describing a list change, for the edit preview. */
export function describeListChange(lc: ListChange): string {
  switch (lc.action) {
    case "setLevel":
      return `Set list level to ${lc.level ?? 0}`;
    case "newList":
      return `Start new list${lc.level ? ` at level ${lc.level}` : ""}`;
    case "continueList": {
      const at = lc.level != null ? ` at level ${lc.level}` : "";
      return `Continue list from paragraph #${lc.anchorParagraphId}${at}`;
    }
    case "detach":
      return "Remove from list";
    default:
      return "List change";
  }
}

/**
 * Tooltip copy for the OUTPUT-LEVEL `numberingChange` gutter icon. Distinct from
 * `describeListChange` (which describes the imperative edit-level `ListChange`):
 * this is display-only descriptive copy for the Word-Ribbon-style indicator and
 * keys off the `NumberingChangeKind` union, not `ListChangeAction`.
 */
export function describeNumberingChange(kind: NumberingChangeKind): string {
  switch (kind) {
    case "indentIn":
      return "Increasing Indent";
    case "indentOut":
      return "Decreasing Indent";
    case "addToList":
      return "Adding to the list";
    case "newList":
      return "Starting a new list";
    default:
      return "Numbering changed";
  }
}
